import { generatePath, type PathParam } from "react-router-dom";

/**
 * Utility type for better intellsense when using `react-router-dom#useParams`.
 * @example
 * const { organizationId } = useParams<RouteParams<RawRoute.organization>>();
 */
export type RouteParams<Path extends string> = { [Key in PathParam<Path>]: Key };

/** Calls generatePath as a curried function with type enforced parameters */
const generatePathWithParams =
  <Path extends string>(originalPath: Path) =>
  (params: Record<PathParam<Path>, string | null>) =>
    generatePath(originalPath, params);

/** Calls generatePath as a curried function with no parameters */
const generatePathNoParams =
  <Path extends string>(originalPath: Path) =>
  () =>
    generatePath(originalPath);

/* eslint-disable sort-keys-fix/sort-keys-fix */
/** This should be used sparingly as it provides little type safety when consuming routes */
export const RawRoute = {
  index: "/",

  // authentication
  login: "/login",
  loginCallback: "/login/google/callback",
  logout: "/logout",
  verify: "/verify",
  home: "/home",

  // admin
  admin: "/admin",
  adminSite: "/admin/site",
  adminSiteDataAnalytics: "/admin/site/platform-insights",
  adminSiteEntityManagement: "/admin/site/access-management",

  // organizations
  organizations: "/admin/organizations",
  myOrganizations: "/my-organizations",
  organization: "/organization/:organizationId",

  // profile
  profile: "/profile",
  profileCreate: "/profile/create",

  // rooms
  myRooms: "/my-rooms",
  podMembers: "/rooms/:roomId/members",
  podUpdate: "/rooms/:roomId/update",
  roomView: "/rooms/:roomId",
  podQr: "/rooms/:roomId/check-in-code",
  podCheckIn: "/rooms/:roomId/check-in",
  pods: "/pods",
  schedules: "/schedules",
  roomSearch: "/search/rooms",
  eventSearch: "/search/events",
  allSearch: "/search/rooms-and-events",
  /**
   * make payments for selected pod in redux
   */
  podPayment: "/pod/payment",

  // events
  myEvents: "/my-events",
  eventCreate: "/events/create",
  eventEdit: "/events/:eventId/update",
  eventMembers: "/events/:eventId/members",
  eventView: "/events/:eventId",
  eventQr: "/events/:eventId/check-in-code",
  eventCheckIn: "/events/:eventId/check-in",
  sessions: "/rooms/:roomId/sessions",

  // resources
  resourceLibrary: "/resource-library",

  // bubbles
  bubbleQr: "/bubbles/:bubbleId/check-in-code",
  bubbleCheckIn: "/bubbles/:bubbleId/check-in",

  // notifications
  notifications: "/notifications",
  notificationsArchived: "/notifications/archived",

  // search
  searchCandidates: "/search/candidates",

  // users
  users: "/users",
  user: "/users/:userId",

  loginDistrict: "/login-district",

  /**
   * implements stripe refresh_url
   *
   * This must match server-side config StripePay::AccountCreationRefreshUrl
   * */
  profileRefresh: "/payment-refresh",

  // ai tutor
  aiTutor: "/ai-tutor",

  // tasks
  taskDashboard: "/tasks",

  /**
   * users are redirected here after successful payments
   *
   * This must match server-side config StripePay::PaymentSuccessUrl
   */
  paymentSuccess: "/payment-success",
  /**
   * users are redirected here after cancelling a payment
   *
   * This must match server-side config StripePay::PaymentCancelUrl
   */
  paymentCancel: "/payment-cancel",
  resumeBuilder: "/resume-builder",
  salesPortalHome: "/sales-portal/home",
  salesPortalPipeline: "/sales-portal/pipeline",
  salesPortalProspects: "/sales-portal/prospects",
  salesPortalProspectDetails: "/sales-portal/prospects/:prospectId",
} as const;
/* eslint-enable sort-keys-fix/sort-keys-fix */

/* eslint-disable sort-keys-fix/sort-keys-fix */
/** Returns type safe route that will inform the user when route parameters are required. */
export const Route = {
  index: generatePathNoParams(RawRoute.index),

  // authentication
  login: generatePathNoParams(RawRoute.login),
  loginCallback: generatePathNoParams(RawRoute.loginCallback),
  logout: generatePathNoParams(RawRoute.logout),
  verify: generatePathNoParams(RawRoute.verify),
  home: generatePathNoParams(RawRoute.home),

  // admin
  admin: generatePathNoParams(RawRoute.admin),
  adminSite: generatePathNoParams(RawRoute.adminSite),
  adminSiteDataAnalytics: generatePathNoParams(RawRoute.adminSiteDataAnalytics),
  adminSiteEntityManagement: generatePathNoParams(RawRoute.adminSiteEntityManagement),

  // organizations
  organizations: generatePathNoParams(RawRoute.organizations),
  myOrganizations: generatePathNoParams(RawRoute.myOrganizations),
  organization: generatePathWithParams(RawRoute.organization),

  // profile
  profile: generatePathNoParams(RawRoute.profile),
  profileCreate: generatePathNoParams(RawRoute.profileCreate),

  aiTutor: generatePathNoParams(RawRoute.aiTutor),

  // tasks
  taskDashboard: generatePathNoParams(RawRoute.taskDashboard),

  // events
  myEvents: generatePathNoParams(RawRoute.myEvents),
  eventCreate: generatePathNoParams(RawRoute.eventCreate),
  eventEdit: generatePathWithParams(RawRoute.eventEdit),
  eventMembers: generatePathWithParams(RawRoute.eventMembers),
  eventView: generatePathWithParams(RawRoute.eventView),
  eventQr: generatePathWithParams(RawRoute.eventQr),
  eventCheckIn: generatePathWithParams(RawRoute.eventCheckIn),
  sessions: generatePathWithParams(RawRoute.sessions),

  // resources
  resourceLibrary: generatePathNoParams(RawRoute.resourceLibrary),

  // rooms
  myRooms: generatePathNoParams(RawRoute.myRooms),
  podMembers: generatePathWithParams(RawRoute.podMembers),
  podUpdate: generatePathWithParams(RawRoute.podUpdate),
  roomView: generatePathWithParams(RawRoute.roomView),
  podQr: generatePathWithParams(RawRoute.podQr),
  podCheckIn: generatePathWithParams(RawRoute.podCheckIn),
  pods: generatePathNoParams(RawRoute.pods),
  schedules: generatePathNoParams(RawRoute.schedules),
  roomSearch: generatePathNoParams(RawRoute.roomSearch),
  eventSearch: generatePathNoParams(RawRoute.eventSearch),
  allSearch: generatePathNoParams(RawRoute.allSearch),
  /**
   * make payments for selected pod in redux
   */
  podPayment: generatePathNoParams(RawRoute.podPayment),

  // bubbles
  bubbleQr: generatePathWithParams(RawRoute.bubbleQr),
  bubbleCheckIn: generatePathWithParams(RawRoute.bubbleCheckIn),

  // notifications
  notifications: generatePathNoParams(RawRoute.notifications),
  notificationsArchived: generatePathNoParams(RawRoute.notificationsArchived),

  loginDistrict: generatePathNoParams(RawRoute.loginDistrict),

  // users
  users: generatePathNoParams(RawRoute.users),
  user: generatePathWithParams(RawRoute.user),
  /**
   * implements stripe refresh_url
   *
   * This must match server-side config StripePay::AccountCreationRefreshUrl
   * */
  profileRefresh: generatePathNoParams(RawRoute.profileRefresh),
  /**
   * users are redirected here after successful payments
   *
   * This must match server-side config StripePay::PaymentSuccessUrl
   */
  paymentSuccess: generatePathNoParams(RawRoute.paymentSuccess),
  /**
   * users are redirected here after cancelling a payment
   *
   * This must match server-side config StripePay::PaymentCancelUrl
   */
  paymentCancel: generatePathNoParams(RawRoute.paymentCancel),
  resumeBuilder: generatePathNoParams(RawRoute.resumeBuilder),
  salesPortalHome: generatePathNoParams(RawRoute.salesPortalHome),
  salesPortalPipeline: generatePathNoParams(RawRoute.salesPortalPipeline),
  salesPortalProspects: generatePathNoParams(RawRoute.salesPortalProspects),
  salesPortalProspectDetails: generatePathWithParams("/sales-portal/prospects/:prospectId"),

  // search
  searchCandidates: generatePathNoParams(RawRoute.searchCandidates),
};
/* eslint-enable sort-keys-fix/sort-keys-fix */
